/**
 * Design tokens, exported as TypeScript so that non-CSS consumers can read the
 * same values.
 *
 * The canonical definitions live in `tokens.css` as custom properties, which is
 * what Tailwind and the components consume. This module mirrors the handful that
 * three.js materials and uPlot axes need, because neither can read a CSS variable:
 * WebGL wants a number, and a canvas chart wants a resolved colour string.
 *
 * Keeping one list in two forms is a real duplication, so it is kept as small as
 * possible and the CSS file is the side that gets edited first.
 */

export type ThemeName = 'dark' | 'light';

export interface ChartPalette {
  /** Plot background. */
  background: string;
  /** Axis lines and labels. */
  axis: string;
  /** Grid lines. */
  grid: string;
  /** Series colours, in assignment order. */
  series: readonly string[];
  /** Colour for an exceeded-limit region. */
  danger: string;
  /** Colour for a caution region. */
  warning: string;
}

/**
 * Series colours are ordered for distinguishability at a glance and remain
 * distinguishable in the most common form of colour vision deficiency, which
 * matters when eight telemetry traces share one axis.
 */
const SERIES_DARK = [
  '#4fa3ff',
  '#ffb648',
  '#5fd3a6',
  '#ff7b72',
  '#c58aff',
  '#7ee0e8',
  '#ffd866',
  '#a0aec0',
] as const;

const SERIES_LIGHT = [
  '#1d6fd0',
  '#c8720a',
  '#0f8a63',
  '#c8382f',
  '#7b3fc4',
  '#0f7f8c',
  '#a17b00',
  '#4a5568',
] as const;

export const CHART_PALETTE: Record<ThemeName, ChartPalette> = {
  dark: {
    background: 'transparent',
    axis: '#8b97a8',
    grid: 'rgba(139, 151, 168, 0.16)',
    series: SERIES_DARK,
    danger: '#ff7b72',
    warning: '#ffb648',
  },
  light: {
    background: 'transparent',
    axis: '#5a6472',
    grid: 'rgba(90, 100, 114, 0.16)',
    series: SERIES_LIGHT,
    danger: '#c8382f',
    warning: '#c8720a',
  },
};

/** Colours the 3D scene needs as numbers rather than CSS strings. */
export const SCENE_COLORS = {
  /** Ocean base colour of the rendered Earth. */
  ocean: 0x0b2f52,
  /** Land base colour. */
  land: 0x2f6b3f,
  /** Ice and high-latitude land. */
  ice: 0xdfe8ee,
  /** Country border lines. */
  border: 0x1a2a38,
  /** Atmospheric rim glow. */
  atmosphere: 0x5fa8e8,
  /** Trajectory line while under power. */
  poweredTrack: 0xffb648,
  /** Trajectory line while coasting. */
  coastTrack: 0x4fa3ff,
  /** Projected orbit ribbon. */
  orbit: 0x5fd3a6,
  /** Ground track on the globe surface. */
  groundTrack: 0xffd866,
  /** Exhaust plume core. */
  exhaustCore: 0xfff3d0,
  /** Exhaust plume edge. */
  exhaustEdge: 0xff7a2f,
} as const;

/** Panel identifiers, so the layout, command palette and shortcuts agree. */
export const PANEL_IDS = [
  'mission-explorer',
  'rocket-editor',
  'orbit-editor',
  'viewport-3d',
  'map-2d',
  'charts',
  'timeline',
  'inspector',
  'analytics',
  'optimizer',
  'transfer-planner',
] as const;

export type PanelId = (typeof PANEL_IDS)[number];

export const PANEL_TITLES: Record<PanelId, string> = {
  'mission-explorer': 'Mission Explorer',
  'rocket-editor': 'Rocket Editor',
  'orbit-editor': 'Orbit Editor',
  'viewport-3d': '3D Viewport',
  'map-2d': 'Ground Track',
  charts: 'Telemetry',
  timeline: 'Timeline',
  inspector: 'Inspector',
  analytics: 'Analytics',
  optimizer: 'Optimizer',
  'transfer-planner': 'Transfer Planner',
};

/** Reader-facing explanations used by navigation, search, and learning UI. */
export const PANEL_DESCRIPTIONS: Record<PanelId, string> = {
  'mission-explorer':
    'Create, search, select, and safely remove mission documents.',
  'rocket-editor':
    'Build staged launch vehicles, choose engines, and inspect mass and delta-v performance.',
  'orbit-editor':
    'Define the target orbit and schedule impulsive maneuvers using orbital elements.',
  'viewport-3d':
    'Inspect Earth, the vehicle, the inertial flight path, and the rotating ground track in 3D.',
  'map-2d':
    'Follow the Earth ground track or the active deep-space reference frame during playback.',
  charts:
    'Run deterministic simulations and compare altitude, speed, loads, mass, attitude, and losses.',
  timeline:
    'Play, pause, seek, and inspect maneuvers and flight events across mission time.',
  inspector:
    'Edit the selected mission, stage, or maneuver and manage exports and version history.',
  analytics:
    'Review performance margins, model fidelity, constraints, risk indicators, and rough costs.',
  optimizer:
    'Search selected ascent-program variables with deterministic numerical optimization.',
  'transfer-planner':
    'Estimate orbit transfers, interplanetary departure requirements, and atmospheric reentry.',
};
